import { createContext, ReactNode, useMemo, useState } from 'react'
import { Dimensions, Range } from 'types'
import { MediaInfo } from '../../../types'
import { DEFAULT_FPS, MAX_PREVIEW_HEIGHT, MAX_PREVIEW_WIDTH } from '../constants'

type ContextProps = {
    aspectRatio: number
    duration: number
    fps: number
    height: number
    paused: boolean
    source: File | string
    timing?: Range
    width: number
    getTime: () => number
    goToTime: (time: number) => void
    setDuration: (duration: number) => void
    setPaused: (paused: boolean) => void
    setTiming: (timing?: Range) => void
}

type Props = {
    children?: ReactNode
    source: File | string
    sourceInfo: MediaInfo
    getTime: () => number
    goToTime: (time: number) => void
}

export const UgeArtboardContext = createContext({} as ContextProps)

const UgeArtboardContextManager = ({ children, source, sourceInfo, getTime, goToTime }: Props) => {
    const { fps = DEFAULT_FPS, height: sourceHeight, width: sourceWidth } = sourceInfo
    const [duration, setDuration] = useState<number>(sourceInfo.duration)
    const [timing, setTiming] = useState<Range | undefined>()
    const [paused, setPaused] = useState<boolean>(false)

    const { height, width }: Dimensions = useMemo(() => {
        const aspectRatio = sourceWidth / sourceHeight
        let width = MAX_PREVIEW_WIDTH
        let height = Math.round(width / aspectRatio)

        if (height > MAX_PREVIEW_HEIGHT) {
            height = MAX_PREVIEW_HEIGHT
            width = Math.round(height * aspectRatio)
        }

        return { height, width }
    }, [sourceHeight, sourceWidth])

    return (
        <UgeArtboardContext.Provider
            value={{
                aspectRatio: width / height,
                duration,
                fps,
                height,
                paused,
                source,
                timing,
                width,
                getTime,
                goToTime,
                setDuration,
                setPaused,
                setTiming,
            }}
        >
            {children}
        </UgeArtboardContext.Provider>
    )
}

export default UgeArtboardContextManager
